import { BarChartAppearance, BarChartData, BarChartProps, Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { TimeRangeDefinition } from '../time-range-types';
import { formatNumber } from '../util';
import { answerRateTooltipRenderer } from './answer-rate-tooltip';
import { compareBarChartData } from './tooltip';

const appearance: BarChartAppearance = {
  showYAxis: true,
  showXAxis: true,

  customXAxisTickFormat(value) {
    return `${value}%`;
  },
  maxHeight: 300,
  groupsGap: 30,

  layout: 'vertical',
};

export const rules = {
  sentiment: (base: number, candidate: number) => {
    const diff = base - candidate;
    if (diff === 0) {
      return 'neutral';
    }
    return diff > 0 ? 'positive' : 'negative';
  },
  direction: (base: number, candidate: number) => {
    const diff = base - candidate;
    if (diff === 0) {
      return 'nochange';
    }
    return diff > 0 ? 'up' : 'down';
  },
  percentage: (base: number, candidate: number) => {
    const diff = base - candidate;
    return Math.abs(diff);
  },
};

export const normalizeData = (data: NonNullable<BarChartProps['data']>) => {
  const normalizedGroups = data.groups.map((group) => {
    const total = Object.values(group.values).reduce((acc, value) => acc + value, 0);

    const normalizedValues =
      total > 0
        ? Object.fromEntries(
            Object.entries(group.values).map(([key, value]) => [key, Math.floor((value / total) * 100)])
          )
        : Object.fromEntries(Object.keys(group.values).map((key) => [key, 0])); // Ensure all values remain 0
    return {
      ...group,
      values: normalizedValues,
    };
  });

  return {
    ...data,
    groups: normalizedGroups,
  };
};

export function AnswerRateChart({
  data,
  pastData,
  groupLabels,
  dateLabel,
  locationLabel,
  timeRangeDefinition,
  isLoading,
  isUsingCdr2Data,
}: {
  data: BarChartData;
  pastData: BarChartData;
  groupLabels: Record<string, string>;
  dateLabel?: string;
  locationLabel?: string;
  timeRangeDefinition: TimeRangeDefinition;
  isLoading?: boolean;
  isUsingCdr2Data?: boolean;
}) {
  const { t } = useTranslation('phone');
  const normalizedData = normalizeData(data);
  const normalizedPastData = normalizeData(pastData);

  const comparisonData = compareBarChartData({
    base: normalizedData,
    candidate: normalizedPastData,
    timeRangeDefinition,
    rules,
  });

  return (
    <>
      <Chart
        isLoading={isLoading}
        colors={{
          answer: theme.colors.success20,
          abandon: theme.colors.critical20,
          timeout: theme.colors.neutral20,
        }}
        labels={{
          answer: t('Answered'),
          abandon: t('Abandoned'),
          timeout: isUsingCdr2Data ? t('Missed') : t('Timed Out'),
          ...groupLabels,
        }}
      >
        <Chart.Header
          title={t('Answer Rate')}
          bottomElement={
            <Text size='medium' as='span' color='subdued'>
              {locationLabel}
            </Text>
          }
          subtitle={dateLabel}
        />
        <Chart.BarChart
          shouldCollapseLegends={false}
          appearance={appearance}
          data={{ ...normalizedData, stackKeys: ['answer', 'abandon', 'timeout'] }}
          tooltipRenderer={answerRateTooltipRenderer({ actualData: data, comparisonData })}
          formatValue={formatNumber}
          yAxis={{ width: 100 }}
        />
        <Chart.Legends />
      </Chart>
    </>
  );
}
